import clsx from "clsx";
import React from "react";
import { Grid2Props, Box, Theme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import {
  ExerciseAssetSort,
  ExerciseAssetType,
} from "@growth-machine-llc/stridist-api-client";
import { LibraryHeaderFilters } from "../header-filters/HeaderFilters";
import { HeaderSelect } from "../header-filters/HeaderSelect";
import { ExerciseAssetTypeLabels } from "./constants";
import ResetFiltersButton from "./filters/ResetFiltersButton";
import { JourneyAnchors } from "../../constants";

type StyleProps = {
  // Handle showing reset and search in one row without type select
  showResetAndSearchInOneRow: boolean;
};

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  inputActive: {
    "&.MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },

    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },

  sort: {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 500,
    flexGrow: 1,
    width: ({ showResetAndSearchInOneRow }) =>
      !showResetAndSearchInOneRow && "100%",
    justifyContent: "flex-end",
    [theme.breakpoints.up("md")]: {
      maxWidth: 400,
    },
    '& [aria-haspopup="listbox"]': {
      color: theme.palette.text.secondary,

      "&::before": {
        content: '"Filter by type: "',
      },
    },
  },

  sortOption: {
    fontWeight: 500,
  },

  rowFilterContainer: {
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
      flexDirection: "row",
    },
  },

  resetFilters: {
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
  },
}));

export interface CoachExerciseLibraryHeaderFiltersProps extends Grid2Props {
  query: string;
  isTagsRoute?: boolean;
  sortKey: ExerciseAssetSort;
  onChangeQuery?: (query: string) => void;
  onChangeSortKey?: (sortKey: ExerciseAssetSort) => void;
  exerciseType?: ExerciseAssetType;
  onChangeExerciseType?: (exerciseType: ExerciseAssetType) => void;
  disabledResetButton?: boolean;
  onResetFilters?: () => void;
  showResetButton?: boolean;
  showFilterByType?: boolean;
  defaultValues: {
    query: string;
    sortKey: ExerciseAssetSort;
    exerciseType: ExerciseAssetType;
  };
}

export function CoachExerciseLibraryHeaderFilters(
  props: CoachExerciseLibraryHeaderFiltersProps,
) {
  const {
    className,
    query,
    onChangeQuery,
    exerciseType,
    onChangeExerciseType,
    isTagsRoute = false,
    defaultValues: { query: defaultQuery, exerciseType: defaultExerciseType },
    disabledResetButton,
    onResetFilters,
    showResetButton,
    showFilterByType,
  } = props;

  const showResetAndSearchInOneRow = showResetButton && !showFilterByType;
  const s = useStyles({ showResetAndSearchInOneRow });
  const handleChangeExerciseType = React.useCallback(
    (event) => {
      const exerciseType = event.target.value as ExerciseAssetType;
      if (onChangeExerciseType) {
        onChangeExerciseType(exerciseType);
      }
    },
    [onChangeExerciseType],
  );

  return (
    <>
      <LibraryHeaderFilters
        query={query}
        onChangeQuery={onChangeQuery}
        className={clsx(
          className,
          showResetAndSearchInOneRow && s.rowFilterContainer,
        )}
        searchPlaceholder={`Search by ${isTagsRoute ? "tags" : "exercises"}`}
        defaultValues={{
          query: defaultQuery,
        }}
        selectItem={
          !isTagsRoute && (
            <Box display="flex" alignItems="center" gap={2} className={s.sort}>
              {showFilterByType && (
                <HeaderSelect
                  value={exerciseType}
                  onChange={handleChangeExerciseType}
                  options={Object.values(ExerciseAssetType)}
                  labels={ExerciseAssetTypeLabels}
                  className={clsx(
                    s.sort,
                    s.inputActive,
                    JourneyAnchors.EXERCISE_LIBRARY_PRIVACY_FILTER,
                  )}
                />
              )}

              {showResetButton && (
                <ResetFiltersButton
                  disabled={disabledResetButton}
                  onClick={onResetFilters}
                  className={clsx(
                    s.resetFilters,
                    JourneyAnchors.EXERCISE_LIBRARY_RESET_FILTERS,
                  )}
                />
              )}
            </Box>
          )
        }
      />
    </>
  );
}
